import { TTrainingDocument, TTrainingEmployee, TTrainingRecordSeed } from '../types';

// Built-in QM training matrix for the "Training records" page. Dates are stored as
// day offsets from today so the demo never goes stale: `completedDaysAgo` for a
// completed training, `dueInDays` (negative = overdue) for an open one.

export const TRAINING_DIRECTORY: TTrainingEmployee[] = [
  { id: 'emp-01', name: 'Anna Fischer', function: 'Production' },
  { id: 'emp-02', name: 'Marco Bianchi', function: 'Production' },
  { id: 'emp-03', name: 'Julia Weber', function: 'Quality Assurance' },
  { id: 'emp-04', name: 'Thomas Keller', function: 'Quality Assurance' },
  { id: 'emp-05', name: 'Sofia Novak', function: 'Regulatory Affairs' },
  { id: 'emp-06', name: 'Lukas Brandt', function: 'Research & Development' },
  { id: 'emp-07', name: 'Emma Laurent', function: 'Research & Development' },
  { id: 'emp-08', name: 'David Huber', function: 'Service & Maintenance' },
  { id: 'emp-09', name: 'Clara Meier', function: 'Management' },
  { id: 'emp-10', name: 'Jonas Wagner', function: 'Production' },
];

// `id: 'configured'` is replaced at runtime by the org's configured training document
// (`mock.training.documentId`), so the approve -> assign flow and this page line up.
export const CONFIGURED_DOCUMENT_ID = 'configured';

export const TRAINING_DOCUMENTS: TTrainingDocument[] = [
  {
    id: CONFIGURED_DOCUMENT_ID,
    name: 'SOP-720 Production',
    revision: 'Revision C',
    trainingMethod: 'Read & acknowledge',
    targetFunctions: ['Production', 'Quality Assurance'],
  },
  {
    id: 'doc-sop-420',
    name: 'SOP-420 Document Control',
    revision: 'Revision F',
    trainingMethod: 'Read & acknowledge',
    targetFunctions: ['Quality Assurance', 'Regulatory Affairs', 'Research & Development', 'Management'],
  },
  {
    id: 'doc-sop-830',
    name: 'SOP-830 Nonconforming Product',
    revision: 'Revision D',
    trainingMethod: 'Classroom training',
    targetFunctions: ['Production', 'Quality Assurance', 'Service & Maintenance'],
  },
  {
    id: 'doc-sop-850',
    name: 'SOP-850 CAPA',
    revision: 'Revision B',
    trainingMethod: 'Assessment / quiz',
    targetFunctions: ['Quality Assurance', 'Management', 'Regulatory Affairs'],
  },
  {
    id: 'doc-wi-721',
    name: 'WI-721 Cleanroom Gowning',
    revision: 'Revision A',
    trainingMethod: 'On-the-job training',
    targetFunctions: ['Production'],
  },
  {
    id: 'doc-sop-740',
    name: 'SOP-740 Supplier Control',
    revision: 'Revision E',
    trainingMethod: 'Read & acknowledge',
    targetFunctions: ['Quality Assurance', 'Management'],
  },
];

export const TRAINING_RECORD_SEEDS: TTrainingRecordSeed[] = [
  // SOP-720 Production (configured document): trained on Revision B, Revision C is
  // new - so the target audience is outdated until training is assigned/completed.
  { employeeId: 'emp-01', documentId: CONFIGURED_DOCUMENT_ID, revision: 'Revision B', completedDaysAgo: 210 },
  { employeeId: 'emp-02', documentId: CONFIGURED_DOCUMENT_ID, revision: 'Revision B', completedDaysAgo: 205 },
  { employeeId: 'emp-03', documentId: CONFIGURED_DOCUMENT_ID, revision: 'Revision B', completedDaysAgo: 198 },

  // SOP-420 Document Control
  { employeeId: 'emp-03', documentId: 'doc-sop-420', revision: 'Revision F', completedDaysAgo: 64 },
  { employeeId: 'emp-04', documentId: 'doc-sop-420', revision: 'Revision F', completedDaysAgo: 61 },
  { employeeId: 'emp-05', documentId: 'doc-sop-420', revision: 'Revision F', completedDaysAgo: 58 },
  { employeeId: 'emp-06', documentId: 'doc-sop-420', revision: 'Revision F', dueInDays: -6 },
  { employeeId: 'emp-07', documentId: 'doc-sop-420', revision: 'Revision F', dueInDays: 9 },
  { employeeId: 'emp-09', documentId: 'doc-sop-420', revision: 'Revision E', completedDaysAgo: 402 },

  // SOP-830 Nonconforming Product
  { employeeId: 'emp-01', documentId: 'doc-sop-830', revision: 'Revision D', completedDaysAgo: 120 },
  { employeeId: 'emp-02', documentId: 'doc-sop-830', revision: 'Revision D', completedDaysAgo: 118 },
  { employeeId: 'emp-03', documentId: 'doc-sop-830', revision: 'Revision D', completedDaysAgo: 117 },
  { employeeId: 'emp-04', documentId: 'doc-sop-830', revision: 'Revision D', completedDaysAgo: 115 },
  { employeeId: 'emp-08', documentId: 'doc-sop-830', revision: 'Revision D', dueInDays: -21 },

  // SOP-850 CAPA
  { employeeId: 'emp-03', documentId: 'doc-sop-850', revision: 'Revision B', completedDaysAgo: 33 },
  { employeeId: 'emp-04', documentId: 'doc-sop-850', revision: 'Revision B', dueInDays: 4 },
  { employeeId: 'emp-09', documentId: 'doc-sop-850', revision: 'Revision B', completedDaysAgo: 30 },

  // WI-721 Cleanroom Gowning
  { employeeId: 'emp-01', documentId: 'doc-wi-721', revision: 'Revision A', completedDaysAgo: 340 },
  { employeeId: 'emp-02', documentId: 'doc-wi-721', revision: 'Revision A', completedDaysAgo: 338 },
  { employeeId: 'emp-10', documentId: 'doc-wi-721', revision: 'Revision A', dueInDays: 12 },

  // SOP-740 Supplier Control
  { employeeId: 'emp-04', documentId: 'doc-sop-740', revision: 'Revision E', completedDaysAgo: 89 },
  { employeeId: 'emp-09', documentId: 'doc-sop-740', revision: 'Revision D', completedDaysAgo: 460 },
];
